/**
 * USER INVOICES COMPONENT
 * 
 * Displays invoices for users who have been issued invoices by admin.
 * Only renders if the user has invoices (conditional rendering in parent).
 * Allows users to view invoice details and request payment for approved invoices.
 */

import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession'
import { downloadInvoicePdf, InvoiceData } from '../../lib/services/invoicePdfService';

// API base URL for server-side endpoints
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface InvoiceLineItem {
  id: number;
  description: string;
  expense_category: string | null;
  quantity: number;
  unit_price: number;
  amount: number;
  expense_date: string | null;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  company_entity: 'SUN' | 'Aureus';
  status: 'draft' | 'pending_approval' | 'approved' | 'paid' | 'rejected' | 'voided';
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  currency?: string;
  description: string | null;
  notes: string | null;
  payment_requested: boolean;
  payment_requested_at: string | null;
  created_at: string;
  line_items?: InvoiceLineItem[];
}

interface UserInvoicesProps {
  userId: number;
  className?: string;
}

export const UserInvoices: React.FC<UserInvoicesProps> = ({ userId, className }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [requestingPayment, setRequestingPayment] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    loadInvoices();
  }, [userId]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/user/dashboard-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ userId, dataTypes: ['invoices_list'] })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)
      setInvoices(result.data?.invoices_list || []);
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError('Failed to load invoices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayment = async (invoiceId: number) => {
    try {
      setRequestingPayment(invoiceId);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({
          userId,
          operation: 'request_invoice_payment',
          data: { invoiceId }
        })
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error)

      setSuccessMessage('Payment request submitted successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadInvoices();
    } catch (err: any) {
      console.error('Error requesting payment:', err);
      setError('Failed to request payment. Please try again.');
    } finally {
      setRequestingPayment(null);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-600 text-white';
      case 'pending_approval': return 'bg-yellow-600 text-yellow-100';
      case 'approved': return 'bg-green-600 text-green-100';
      case 'paid': return 'bg-blue-600 text-blue-100';
      case 'rejected': return 'bg-red-600 text-red-100';
      case 'voided': return 'bg-gray-500 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;
  
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft': return 'Draft';
      case 'pending_approval': return 'Pending Approval';
      case 'approved': return 'Approved';
      case 'paid': return 'Paid';
      case 'rejected': return 'Rejected';
      case 'voided': return 'Voided';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className={`bg-gray-800 rounded-lg border border-gray-700 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
          <span className="ml-3 text-white">Loading invoices...</span>
        </div>
      </div>
    );
  }

  if (invoices.length === 0) {
    return null; // Don't render anything if no invoices
  }

  return (
    <div className={`bg-gray-800 rounded-lg border border-gray-700 p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            📄 My Invoices
          </h2>
          <p className="text-white text-sm mt-1">
            View your invoices and request payments
          </p>
        </div>
        <button
          onClick={loadInvoices}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-600/20 border border-red-500 rounded-lg text-red-400">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-600/20 border border-green-500 rounded-lg text-green-400">
          {successMessage}
        </div>
      )}

      {/* Invoice List */}
      <div className="space-y-3">
        {invoices.map((invoice) => (
          <div
            key={invoice.id}
            className="bg-gray-900 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white font-mono font-semibold">{invoice.invoice_number}</span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusBadgeColor(invoice.status)}`}>
                    {getStatusLabel(invoice.status)}
                  </span>
                  <span className="text-white text-sm">
                    {invoice.company_entity === 'SUN' ? '☀️ SUN' : '🏆 Aureus'}
                  </span>
                </div>
                <p className="text-white text-sm mt-1">
                  {formatDate(invoice.invoice_date)}
                  {invoice.description && <span className="ml-2">• {invoice.description}</span>}
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-yellow-500 font-bold text-lg">{formatCurrency(invoice.total_amount)}</div>
                  {invoice.payment_requested && invoice.status === 'approved' && (
                    <span className="text-blue-400 text-sm">Payment Requested</span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedInvoice(invoice)}
                    className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
                  >
                    View
                  </button>
                  {invoice.status === 'approved' && !invoice.payment_requested && (
                    <button
                      onClick={() => handleRequestPayment(invoice.id)}
                      disabled={requestingPayment === invoice.id}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg text-sm transition-colors"
                    >
                      {requestingPayment === invoice.id ? '...' : 'Request Payment'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{selectedInvoice.invoice_number}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${getStatusBadgeColor(selectedInvoice.status)}`}>
                      {getStatusLabel(selectedInvoice.status)}
                    </span>
                    <span className="text-white text-sm">
                      {selectedInvoice.company_entity === 'SUN' ? '☀️ SUN' : '🏆 Aureus'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ×
                </button>
              </div>

              {/* Invoice Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <span className="text-gray-500 text-sm">Invoice Date</span>
                  <p className="text-white">{formatDate(selectedInvoice.invoice_date)}</p>
                </div>
                {selectedInvoice.due_date && (
                  <div>
                    <span className="text-gray-500 text-sm">Due Date</span>
                    <p className="text-white">{formatDate(selectedInvoice.due_date)}</p>
                  </div>
                )}
                {selectedInvoice.description && (
                  <div className="col-span-2">
                    <span className="text-gray-500 text-sm">Description</span>
                    <p className="text-white">{selectedInvoice.description}</p>
                  </div>
                )}
              </div>

              {/* Line Items */}
              <div className="mb-6">
                <h4 className="text-white font-semibold mb-3">Line Items</h4>
                <div className="bg-gray-900 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-700">
                      <tr>
                        <th className="text-left text-gray-300 px-4 py-2 text-sm">Description</th>
                        <th className="text-right text-gray-300 px-4 py-2 text-sm">Qty</th>
                        <th className="text-right text-gray-300 px-4 py-2 text-sm">Price</th>
                        <th className="text-right text-gray-300 px-4 py-2 text-sm">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.line_items?.map((item) => (
                        <tr key={item.id} className="border-t border-gray-700">
                          <td className="px-4 py-3 text-white">{item.description}</td>
                          <td className="px-4 py-3 text-gray-300 text-right">{item.quantity}</td>
                          <td className="px-4 py-3 text-gray-300 text-right">{formatCurrency(item.unit_price)}</td>
                          <td className="px-4 py-3 text-white text-right">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="bg-gray-900 rounded-lg p-4 mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-white">Subtotal</span>
                  <span className="text-white">{formatCurrency(selectedInvoice.subtotal)}</span>
                </div>
                {selectedInvoice.tax_rate > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-white">Tax ({selectedInvoice.tax_rate}%)</span>
                    <span className="text-white">{formatCurrency(selectedInvoice.tax_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-700">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-yellow-500 font-bold text-xl">{formatCurrency(selectedInvoice.total_amount)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                {selectedInvoice.status === 'approved' && !selectedInvoice.payment_requested && (
                  <button
                    onClick={() => {
                      handleRequestPayment(selectedInvoice.id);
                      setSelectedInvoice(null);
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    Request Payment
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!selectedInvoice) return;
                    try {
                      const invoiceData: InvoiceData = {
                        invoice_number: selectedInvoice.invoice_number,
                        invoice_date: selectedInvoice.invoice_date,
                        due_date: selectedInvoice.due_date,
                        company_entity: selectedInvoice.company_entity,
                        status: selectedInvoice.status,
                        subtotal: selectedInvoice.subtotal,
                        tax_rate: selectedInvoice.tax_rate,
                        tax_amount: selectedInvoice.tax_amount,
                        total_amount: selectedInvoice.total_amount,
                        currency: selectedInvoice.currency || 'USD',
                        description: selectedInvoice.description,
                        notes: selectedInvoice.notes,
                        user: {
                          full_name: null, // User viewing their own invoice
                          username: 'User',
                          email: ''
                        },
                        line_items: (selectedInvoice.line_items || []).map(li => ({
                          description: li.description,
                          expense_category: li.expense_category,
                          quantity: li.quantity,
                          unit_price: li.unit_price,
                          amount: li.amount,
                          expense_date: li.expense_date
                        }))
                      };
                      await downloadInvoicePdf(invoiceData);
                      setSuccessMessage('PDF downloaded successfully');
                      setTimeout(() => setSuccessMessage(null), 3000);
                    } catch (err) {
                      console.error('Error downloading PDF:', err);
                      setError('Failed to download PDF');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  📥 Download PDF
                </button>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInvoices;
